'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type ActionMode = 'blur' | 'pixelate' | 'fill';
type EditorInteractionMode = 'select' | 'move';
type ImageBlurPixelateVariant = {
  lockedActionMode?: ActionMode;
  hideActionChooser?: boolean;
  badgeLabel?: string;
  toolSlug?: string;
  defaultFillColor?: string;
  defaultBlurRadius?: number;
  defaultPixelSize?: number;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

type SelectionRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type EditAction =
  | {
      kind: 'blur';
      rect: SelectionRect;
      radius: number;
    }
  | {
      kind: 'pixelate';
      rect: SelectionRect;
      blockSize: number;
    }
  | {
      kind: 'fill';
      rect: SelectionRect;
      color: string;
    };

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const actionModes: Array<{
  value: ActionMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'blur',
    label: 'Blur area',
    hint: 'Softens faces, UI details, and private data without removing the whole section.',
  },
  {
    value: 'pixelate',
    label: 'Pixelate area',
    hint: 'Good for redacting screenshots, numbers, or faces with a more explicit mask.',
  },
  {
    value: 'fill',
    label: 'Solid fill',
    hint: 'Covers a selected rectangle with a flat color for stronger redaction.',
  },
];

function QuickActionIcon({ mode }: { mode: ActionMode }) {
  if (mode === 'blur') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M12 5.5a6.5 6.5 0 1 0 6.5 6.5"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
        <path
          d="M17.5 4.5h2v2"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
        <path
          d="M19.5 4.5 16 8"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (mode === 'pixelate') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M4 4h5v5H4zM10 4h4v4h-4zM15 4h5v5h-5zM4 10h4v4H4zM9 9h6v6H9zM16 10h4v4h-4zM4 15h5v5H4zM10 16h4v4h-4zM15 15h5v5h-5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M5 6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 17.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function RevertIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M9 7H5v4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <path
        d="M5 11a7 7 0 1 0 2.05-4.95L5 7"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function sanitizeSelection(rect: SelectionRect, width: number, height: number) {
  const startX = clamp(Math.round(rect.x), 0, width - 1);
  const startY = clamp(Math.round(rect.y), 0, height - 1);
  const endX = clamp(Math.round(rect.x + rect.width), startX + 1, width);
  const endY = clamp(Math.round(rect.y + rect.height), startY + 1, height);

  return {
    x: startX,
    y: startY,
    width: Math.max(1, endX - startX),
    height: Math.max(1, endY - startY),
  };
}

function buildRectFromPoints(startX: number, startY: number, endX: number, endY: number) {
  return {
    x: Math.min(startX, endX),
    y: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  };
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function drawPreview(
  source: CanvasImageSource,
  target: HTMLCanvasElement | null,
  width: number,
  height: number,
) {
  if (!target) {
    return;
  }

  target.width = width;
  target.height = height;

  const context = target.getContext('2d');

  if (!context) {
    return;
  }

  context.clearRect(0, 0, width, height);
  context.drawImage(source, 0, 0, width, height);
}

function applyBlurAction(context: CanvasRenderingContext2D, rect: SelectionRect, radius: number) {
  const padding = Math.max(8, Math.ceil(radius * 3));
  const sampleX = Math.max(0, rect.x - padding);
  const sampleY = Math.max(0, rect.y - padding);
  const sampleWidth = Math.min(context.canvas.width - sampleX, rect.width + padding * 2);
  const sampleHeight = Math.min(context.canvas.height - sampleY, rect.height + padding * 2);
  const sourceCanvas = createCanvas(sampleWidth, sampleHeight);
  const sourceContext = sourceCanvas.getContext('2d');

  if (!sourceContext) {
    return;
  }

  sourceContext.drawImage(
    context.canvas,
    sampleX,
    sampleY,
    sampleWidth,
    sampleHeight,
    0,
    0,
    sampleWidth,
    sampleHeight,
  );

  const blurredCanvas = createCanvas(sampleWidth, sampleHeight);
  const blurredContext = blurredCanvas.getContext('2d');

  if (!blurredContext) {
    return;
  }

  blurredContext.filter = `blur(${radius}px)`;
  blurredContext.drawImage(sourceCanvas, 0, 0);
  blurredContext.filter = 'none';
  context.drawImage(
    blurredCanvas,
    rect.x - sampleX,
    rect.y - sampleY,
    rect.width,
    rect.height,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
  );
}

function applyPixelateAction(
  context: CanvasRenderingContext2D,
  rect: SelectionRect,
  blockSize: number,
) {
  const sourceCanvas = createCanvas(rect.width, rect.height);
  const sourceContext = sourceCanvas.getContext('2d');

  if (!sourceContext) {
    return;
  }

  sourceContext.drawImage(
    context.canvas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    rect.width,
    rect.height,
  );

  const scaledWidth = Math.max(1, Math.round(rect.width / blockSize));
  const scaledHeight = Math.max(1, Math.round(rect.height / blockSize));
  const pixelCanvas = createCanvas(scaledWidth, scaledHeight);
  const pixelContext = pixelCanvas.getContext('2d');

  if (!pixelContext) {
    return;
  }

  pixelContext.imageSmoothingEnabled = true;
  pixelContext.drawImage(
    sourceCanvas,
    0,
    0,
    rect.width,
    rect.height,
    0,
    0,
    scaledWidth,
    scaledHeight,
  );

  context.save();
  context.imageSmoothingEnabled = false;
  context.drawImage(
    pixelCanvas,
    0,
    0,
    scaledWidth,
    scaledHeight,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
  );
  context.restore();
}

function applyFillAction(context: CanvasRenderingContext2D, rect: SelectionRect, color: string) {
  context.save();
  context.fillStyle = color;
  context.fillRect(rect.x, rect.y, rect.width, rect.height);
  context.restore();
}

function renderEditedCanvas(image: LoadedImage, actions: EditAction[]) {
  const canvas = createCanvas(image.width, image.height);
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare the editor.');
  }

  context.drawImage(image.element, 0, 0, image.width, image.height);

  actions.forEach((action) => {
    if (action.kind === 'blur') {
      applyBlurAction(context, action.rect, action.radius);
      return;
    }

    if (action.kind === 'pixelate') {
      applyPixelateAction(context, action.rect, action.blockSize);
      return;
    }

    applyFillAction(context, action.rect, action.color);
  });

  return canvas;
}

function formatSelectionLabel(selection: SelectionRect | null) {
  if (!selection) {
    return 'Draw a rectangle on the preview';
  }

  return `${selection.width} × ${selection.height}px`;
}

function formatActionLabel(action: EditAction) {
  if (action.kind === 'blur') {
    return `Blur • ${action.rect.width} × ${action.rect.height}px • ${action.radius}px radius`;
  }

  if (action.kind === 'pixelate') {
    return `Pixelate • ${action.rect.width} × ${action.rect.height}px • ${action.blockSize}px blocks`;
  }

  return `Solid fill • ${action.rect.width} × ${action.rect.height}px • ${action.color.toUpperCase()}`;
}

function buildEditAction(
  mode: ActionMode,
  rect: SelectionRect,
  blurRadius: number,
  pixelSize: number,
  fillColor: string,
): EditAction {
  if (mode === 'blur') {
    return { kind: 'blur', rect, radius: blurRadius };
  }

  if (mode === 'pixelate') {
    return { kind: 'pixelate', rect, blockSize: pixelSize };
  }

  return { kind: 'fill', rect, color: fillColor };
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function normalizeHexColor(color: string) {
  return color.trim().toLowerCase();
}

type BrowserImageBlurPixelateProps = {
  variant?: ImageBlurPixelateVariant;
};

export default function BrowserImageBlurPixelate({ variant }: BrowserImageBlurPixelateProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const editorCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const beforeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const afterCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const workingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const lockedActionMode = variant?.lockedActionMode ?? null;
  const visibleActionModes = useMemo(
    () =>
      lockedActionMode
        ? actionModes.filter((mode) => mode.value === lockedActionMode)
        : actionModes,
    [lockedActionMode],
  );
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [actions, setActions] = useState<EditAction[]>([]);
  const [activeMode, setActiveMode] = useState<ActionMode>(lockedActionMode ?? 'blur');
  const [blurRadius, setBlurRadius] = useState(variant?.defaultBlurRadius ?? 18);
  const [pixelSize, setPixelSize] = useState(variant?.defaultPixelSize ?? 18);
  const [fillColor, setFillColor] = useState(variant?.defaultFillColor ?? '#111827');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.92);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [interactionMode, setInteractionMode] = useState<EditorInteractionMode>('select');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const editorCanvasSize = useMemo(
    () =>
      loadedImage
        ? {
            width: loadedImage.width,
            height: loadedImage.height,
          }
        : null,
    [loadedImage],
  );
  const comparePreviewSize = useMemo(
    () => (loadedImage ? getContainSize(loadedImage.width, loadedImage.height, 1080, 720) : null),
    [loadedImage],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activeModeMeta = actionModes.find((mode) => mode.value === activeMode) ?? actionModes[0];
  const normalizedFillColor = normalizeHexColor(fillColor);
  const isBlackBoxMode = activeMode === 'fill' && normalizedFillColor === '#000000';
  const isWhiteBoxMode = activeMode === 'fill' && normalizedFillColor === '#ffffff';
  const isFillColorLocked =
    lockedActionMode === 'fill' &&
    Boolean(variant?.defaultFillColor) &&
    visibleActionModes.length === 1;
  const singleModeActive = visibleActionModes.length === 1;
  const editorTitle = singleModeActive
    ? activeMode === 'blur'
      ? 'Blur parts of an image in the browser'
      : activeMode === 'pixelate'
        ? 'Pixelate parts of an image in the browser'
        : isBlackBoxMode
          ? 'Cover parts of an image with black boxes'
          : isWhiteBoxMode
            ? 'Cover parts of an image with white boxes'
            : 'Cover parts of an image in the browser'
    : 'Blur, pixelate, or fill areas in the browser';
  const editorDescription = singleModeActive
    ? activeMode === 'blur'
      ? 'Upload one image, drag a rectangle over the sensitive area, blur it locally, and export the cleaned result right away.'
      : activeMode === 'pixelate'
        ? 'Upload one image, draw a rectangle over the part to hide, pixelate it locally, and export the cleaned result right away.'
        : isBlackBoxMode
          ? 'Upload one image, place black boxes over the sensitive parts, and export the redacted result without sending the file to a server.'
          : isWhiteBoxMode
            ? 'Upload one image, place white boxes over the sensitive parts, and export the redacted result without sending the file to a server.'
            : 'Upload one image, cover the sensitive parts with a solid fill, and export the cleaned result right away.'
    : 'Upload one image, drag a rectangle over the sensitive area, stack edits fast, and export the cleaned result right away.';
  const panelTitle = singleModeActive
    ? activeMode === 'blur'
      ? 'Fast local blur'
      : activeMode === 'pixelate'
        ? 'Fast local pixelation'
        : 'Fast local redaction'
    : 'Fast local redaction';
  const applyActionLabel =
    activeMode === 'blur'
      ? 'Apply blur'
      : activeMode === 'pixelate'
        ? 'Apply pixelate'
        : isBlackBoxMode
          ? 'Apply black box'
          : isWhiteBoxMode
            ? 'Apply white box'
            : 'Apply solid fill';
  const selectionStyle =
    loadedImage && selection
      ? {
          left: `${(selection.x / loadedImage.width) * 100}%`,
          top: `${(selection.y / loadedImage.height) * 100}%`,
          width: `${(selection.width / loadedImage.width) * 100}%`,
          height: `${(selection.height / loadedImage.height) * 100}%`,
        }
      : null;
  const selectionActionsBelow =
    loadedImage && selection ? selection.y / loadedImage.height < 0.14 : false;
  const selectionActionsSide =
    loadedImage && selection
      ? selection.x / loadedImage.width < 0.22
        ? 'left'
        : (selection.x + selection.width) / loadedImage.width > 0.78
          ? 'right'
          : 'center'
      : 'center';

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  useEffect(() => {
    if (!loadedImage) {
      setZoomLevel(1);
    }
  }, [loadedImage]);

  useEffect(() => {
    if (!lockedActionMode) {
      return;
    }

    setActiveMode(lockedActionMode);
  }, [lockedActionMode]);

  useEffect(() => {
    if (variant?.defaultFillColor) {
      setFillColor(variant.defaultFillColor);
    }
  }, [variant?.defaultFillColor]);

  useEffect(() => {
    if (variant?.defaultBlurRadius !== undefined) {
      setBlurRadius(variant.defaultBlurRadius);
    }
  }, [variant?.defaultBlurRadius]);

  useEffect(() => {
    if (variant?.defaultPixelSize !== undefined) {
      setPixelSize(variant.defaultPixelSize);
    }
  }, [variant?.defaultPixelSize]);

  useEffect(() => {
    if (!loadedImage || !editorCanvasSize || !comparePreviewSize) {
      workingCanvasRef.current = null;
      return;
    }

    const nextWorkingCanvas = renderEditedCanvas(loadedImage, actions);
    workingCanvasRef.current = nextWorkingCanvas;
    drawPreview(
      loadedImage.element,
      beforeCanvasRef.current,
      comparePreviewSize.width,
      comparePreviewSize.height,
    );
    drawPreview(
      nextWorkingCanvas,
      editorCanvasRef.current,
      editorCanvasSize.width,
      editorCanvasSize.height,
    );
    drawPreview(
      nextWorkingCanvas,
      afterCanvasRef.current,
      comparePreviewSize.width,
      comparePreviewSize.height,
    );
  }, [actions, comparePreviewSize, editorCanvasSize, loadedImage]);

  useEffect(() => {
    if (!loadedImage || !editorCanvasSize) {
      return;
    }

    requestAnimationFrame(() => fitEditorToViewport());
  }, [editorCanvasSize, loadedImage]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setSelection(null);
    setActions([]);
    setIsSelecting(false);
    setIsLoadingImage(false);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    setIsLoadingImage(true);

    try {
      const image = await loadImageFromFile(file);
      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    } finally {
      setIsLoadingImage(false);
    }
  }

  function getImagePoint(event: ReactPointerEvent<HTMLDivElement>) {
    if (!loadedImage) {
      return null;
    }

    const bounds = event.currentTarget.getBoundingClientRect();

    if (!bounds.width || !bounds.height) {
      return null;
    }

    return {
      x: clamp(
        ((event.clientX - bounds.left) / bounds.width) * loadedImage.width,
        0,
        loadedImage.width,
      ),
      y: clamp(
        ((event.clientY - bounds.top) / bounds.height) * loadedImage.height,
        0,
        loadedImage.height,
      ),
    };
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!loadedImage || isLoadingImage || interactionMode === 'move') {
      return;
    }

    const point = getImagePoint(event);

    if (!point) {
      return;
    }

    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: point.x,
      y: point.y,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
    setIsSelecting(true);
    setSelection(
      sanitizeSelection(
        { x: point.x, y: point.y, width: 1, height: 1 },
        loadedImage.width,
        loadedImage.height,
      ),
    );
    setErrorMessage(null);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (
      !loadedImage ||
      interactionMode === 'move' ||
      !pointerStartRef.current ||
      pointerStartRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const point = getImagePoint(event);

    if (!point) {
      return;
    }

    const nextSelection = sanitizeSelection(
      buildRectFromPoints(pointerStartRef.current.x, pointerStartRef.current.y, point.x, point.y),
      loadedImage.width,
      loadedImage.height,
    );

    setSelection(nextSelection);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (
      !loadedImage ||
      interactionMode === 'move' ||
      !pointerStartRef.current ||
      pointerStartRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const point = getImagePoint(event);

    if (point) {
      const nextSelection = sanitizeSelection(
        buildRectFromPoints(pointerStartRef.current.x, pointerStartRef.current.y, point.x, point.y),
        loadedImage.width,
        loadedImage.height,
      );

      setSelection(nextSelection.width < 6 || nextSelection.height < 6 ? null : nextSelection);
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    pointerStartRef.current = null;
    setIsSelecting(false);
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>) {
    if (pointerStartRef.current?.pointerId === event.pointerId) {
      pointerStartRef.current = null;
    }

    setIsSelecting(false);
  }

  function applySelectionAction(mode: ActionMode = activeMode) {
    if (!loadedImage || !selection) {
      setErrorMessage('Draw a rectangular selection first.');
      return;
    }

    const nextRect = sanitizeSelection(selection, loadedImage.width, loadedImage.height);

    if (nextRect.width < 6 || nextRect.height < 6) {
      setErrorMessage('Make the selection a bit larger before applying an edit.');
      return;
    }

    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setActiveMode(mode);
    setActions((current) => [
      ...current,
      buildEditAction(mode, nextRect, blurRadius, pixelSize, fillColor),
    ]);
  }

  function undoLastAction() {
    if (!actions.length) {
      return;
    }

    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setActions((current) => current.slice(0, -1));
  }

  function resetAll() {
    resetOutputState();
    setCreditMessage(null);
    setErrorMessage(null);
    setSelection(null);
    setActions([]);
  }

  function fitEditorToViewport() {
    if (!editorCanvasSize || !editorFrameRef.current) {
      return;
    }

    const frameStyles = window.getComputedStyle(editorFrameRef.current);
    const horizontalPadding =
      Number.parseFloat(frameStyles.paddingLeft || '0') +
      Number.parseFloat(frameStyles.paddingRight || '0');
    const verticalPadding =
      Number.parseFloat(frameStyles.paddingTop || '0') +
      Number.parseFloat(frameStyles.paddingBottom || '0');
    const availableWidth = Math.max(120, editorFrameRef.current.clientWidth - horizontalPadding);
    const availableHeight = Math.max(120, editorFrameRef.current.clientHeight - verticalPadding);
    const nextZoom = clamp(
      Math.min(availableWidth / editorCanvasSize.width, availableHeight / editorCanvasSize.height),
      0.2,
      4,
    );

    setZoomLevel(nextZoom);
  }

  function adjustZoom(delta: number) {
    setZoomLevel((current) => clamp(Number((current + delta).toFixed(2)), 0.2, 4));
  }

  async function runExport() {
    if (!loadedImage || !workingCanvasRef.current) {
      setErrorMessage('Choose an image first.');
      return;
    }

    if (!actions.length) {
      setErrorMessage('Apply at least one blur, pixelate, or fill action before exporting.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'image-blur-pixelate' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const blob = await new Promise<Blob | null>((resolve) => {
        workingCanvasRef.current?.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The export could not finish for this image.');
      }

      resetOutputState();

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-edited.${selectedOutput.extension}`;
      triggerDownload(nextDownloadUrl, nextFileName);

      setDownloadUrl(nextDownloadUrl);
      setDownloadName(nextFileName);
      setResultSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The edited image could not be exported.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const blurCount = actions.filter((action) => action.kind === 'blur').length;
  const pixelateCount = actions.filter((action) => action.kind === 'pixelate').length;
  const fillCount = actions.filter((action) => action.kind === 'fill').length;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{editorTitle}</h2>
          <p>{editorDescription}</p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Client-side'}</span>
      </div>

      {variant?.hideActionChooser || visibleActionModes.length < 2 ? null : (
        <div className="tool-live-routes" aria-label="Edit modes">
          {visibleActionModes.map((mode) => (
            <button
              key={mode.value}
              className={`tool-live-route ${activeMode === mode.value ? 'tool-live-route-active' : ''}`}
              onClick={() => {
                setActiveMode(mode.value);
                setErrorMessage(null);
              }}
              type="button"
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}

      <div className="tool-live-preview tool-editor-preview tool-image-editor-shell">
        <div className="tool-image-editor-header">
          <h3>Editor</h3>
          <div className="tool-image-editor-header-actions">
            <div
              className="tool-image-editor-mode-switch"
              aria-label="Editor interaction mode"
              role="group"
            >
              <button
                className={`tool-image-editor-mode-button ${interactionMode === 'select' ? 'tool-image-editor-mode-button-active' : ''}`}
                onClick={() => setInteractionMode('select')}
                type="button"
              >
                Select
              </button>
              <button
                className={`tool-image-editor-mode-button ${interactionMode === 'move' ? 'tool-image-editor-mode-button-active' : ''}`}
                onClick={() => setInteractionMode('move')}
                type="button"
              >
                Move
              </button>
            </div>
            <div className="tool-image-zoom-controls" aria-label="Zoom controls" role="group">
              <button
                className="button button-ghost tool-image-zoom-button"
                disabled={!editorCanvasSize || zoomLevel <= 0.21}
                onClick={() => adjustZoom(-0.25)}
                type="button"
              >
                -
              </button>
              <span className="tool-image-zoom-readout">{Math.round(zoomLevel * 100)}%</span>
              <button
                className="button button-ghost tool-image-zoom-button"
                disabled={!editorCanvasSize || zoomLevel >= 3.99}
                onClick={() => adjustZoom(0.25)}
                type="button"
              >
                +
              </button>
              <button
                className="button button-ghost tool-image-zoom-fit"
                disabled={!editorCanvasSize}
                onClick={fitEditorToViewport}
                type="button"
              >
                Fit
              </button>
            </div>
            <button
              className="button button-ghost tool-image-editor-revert"
              disabled={!actions.length}
              onClick={undoLastAction}
              type="button"
            >
              <RevertIcon />
              <span>Revert</span>
            </button>
          </div>
        </div>
        <div
          ref={editorFrameRef}
          className="tool-live-preview-frame tool-editor-preview-frame tool-image-editor-frame"
        >
          {loadedImage && editorCanvasSize ? (
            <div
              className={`tool-image-editor-viewport ${interactionMode === 'move' ? 'tool-image-editor-viewport-pan' : ''}`}
              onPointerCancel={interactionMode === 'select' ? handlePointerCancel : undefined}
              onPointerDown={interactionMode === 'select' ? handlePointerDown : undefined}
              onPointerMove={interactionMode === 'select' ? handlePointerMove : undefined}
              onPointerUp={interactionMode === 'select' ? handlePointerUp : undefined}
              style={{
                width: `${Math.round(editorCanvasSize.width * zoomLevel)}px`,
                height: `${Math.round(editorCanvasSize.height * zoomLevel)}px`,
              }}
            >
              <canvas
                ref={editorCanvasRef}
                aria-hidden="true"
                className="tool-image-editor-canvas"
              />
              {selectionStyle ? (
                <div className="tool-image-editor-selection" style={selectionStyle}>
                  <span className="tool-image-editor-selection-label">
                    {formatSelectionLabel(selection)}
                  </span>
                  {!isSelecting ? (
                    <div
                      aria-label="Quick apply actions"
                      className={`tool-image-editor-selection-actions ${selectionActionsBelow ? 'tool-image-editor-selection-actions-below' : ''} ${selectionActionsSide === 'left' ? 'tool-image-editor-selection-actions-left' : selectionActionsSide === 'right' ? 'tool-image-editor-selection-actions-right' : ''}`}
                      role="toolbar"
                    >
                      {visibleActionModes.map((mode) => (
                        <button
                          key={mode.value}
                          aria-label={mode.label}
                          className={`tool-image-editor-selection-action ${activeMode === mode.value ? 'tool-image-editor-selection-action-active' : ''}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            applySelectionAction(mode.value);
                          }}
                          onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onPointerUp={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          title={mode.label}
                          type="button"
                        >
                          <QuickActionIcon mode={mode.value} />
                          <span>
                            {mode.value === 'pixelate'
                              ? 'Pixel'
                              : mode.value === 'fill'
                                ? normalizedFillColor === '#000000'
                                  ? 'Black'
                                  : normalizedFillColor === '#ffffff'
                                    ? 'White'
                                    : 'Solid'
                                : 'Blur'}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="tool-editor-empty">
              <strong>{isLoadingImage ? 'Loading preview' : 'Preview appears here'}</strong>
              <span>
                {isLoadingImage
                  ? 'Preparing the image for local editing.'
                  : 'Upload an image, then drag to create a rectangular selection.'}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {isLoadingImage
                  ? 'Loading image…'
                  : loadedImage
                    ? loadedImage.name
                    : 'Choose an image or drop it here'}
              </strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>From</span>
              <input
                disabled
                readOnly
                type="text"
                value={
                  loadedImage ? normalizeSourceLabel(loadedImage.type) : 'Auto-detect after upload'
                }
              />
            </label>
            <label className="field">
              <span>Export as</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.45"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `${Math.round(quality * 100)}% quality`
                  : 'PNG keeps the clean export without a quality slider'}
              </small>
            </label>
            <label className="field">
              <span>Selection</span>
              <input disabled readOnly type="text" value={formatSelectionLabel(selection)} />
            </label>
          </div>

          <div className="tool-image-toolbar">
            <div className="tool-live-controls">
              {activeMode === 'blur' ? (
                <label className="field tool-live-quality">
                  <span>Blur radius</span>
                  <input
                    max="40"
                    min="4"
                    onChange={(event) => setBlurRadius(Number(event.target.value))}
                    step="1"
                    type="range"
                    value={blurRadius}
                  />
                  <small>{blurRadius}px blur radius</small>
                </label>
              ) : null}
              {activeMode === 'pixelate' ? (
                <label className="field tool-live-quality">
                  <span>Pixel size</span>
                  <input
                    max="48"
                    min="4"
                    onChange={(event) => setPixelSize(Number(event.target.value))}
                    step="1"
                    type="range"
                    value={pixelSize}
                  />
                  <small>{pixelSize}px pixel blocks</small>
                </label>
              ) : null}
              {activeMode === 'fill' ? (
                <label className="field">
                  <span>Fill color</span>
                  <input
                    className="tool-image-color-input"
                    disabled={isFillColorLocked}
                    onChange={(event) => setFillColor(event.target.value)}
                    type="color"
                    value={fillColor}
                  />
                </label>
              ) : null}
              <div className="tool-live-panel-note">
                <strong>{activeModeMeta.label}</strong>
                <span>{activeModeMeta.hint}</span>
              </div>
            </div>

            <div className="tool-image-toolbar-actions">
              <button
                className="button"
                disabled={!loadedImage || !selection}
                onClick={() => applySelectionAction()}
                type="button"
              >
                {applyActionLabel}
              </button>
              <button
                className="button button-ghost"
                disabled={!actions.length}
                onClick={undoLastAction}
                type="button"
              >
                Revert
              </button>
              <button
                className="button button-ghost"
                disabled={!actions.length && !selection}
                onClick={resetAll}
                type="button"
              >
                Reset all
              </button>
              <button
                className="button tool-image-export-button"
                disabled={!loadedImage || !actions.length || isExporting}
                onClick={() => void runExport()}
                type="button"
              >
                {isExporting ? 'Exporting…' : `Export as ${selectedOutput.label}`}
              </button>
              <p className="tool-image-toolbar-note">
                Draw directly on the preview to create a rectangular selection. Each edit stays
                editable through undo history.
              </p>
            </div>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>{panelTitle}</h3>
          <div className="tool-mockup-chip-list">
            {singleModeActive ? (
              <span>
                {activeMode === 'blur'
                  ? 'Blur'
                  : activeMode === 'pixelate'
                    ? 'Pixelate'
                    : isBlackBoxMode
                      ? 'Black box'
                      : isWhiteBoxMode
                        ? 'White box'
                        : 'Solid fill'}
              </span>
            ) : (
              <>
                <span>Blur</span>
                <span>Pixelate</span>
                <span>Solid fill</span>
              </>
            )}
            <span>Rectangular selection</span>
          </div>
          <p>
            {singleModeActive
              ? 'Everything runs in the browser. The source image stays on the device while you hide the sensitive part and prepare the export.'
              : 'Everything runs in the browser. Nothing is uploaded to a server while you prepare the redacted export.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to PNG, JPG, or WebP.</span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Resolution</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Selection</strong>
                <span>{formatSelectionLabel(selection)}</span>
              </div>
              <div>
                <strong>Actions</strong>
                <span>{actions.length ? `${actions.length} applied` : 'No edits yet'}</span>
              </div>
              <div>
                <strong>Mode mix</strong>
                <span>{`${blurCount} blur • ${pixelateCount} pixelate • ${fillCount} fill`}</span>
              </div>
              <div>
                <strong>Export</strong>
                <span>{selectedOutput.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Result size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {actions.length ? (
            <div className="tool-batch-list">
              {actions
                .slice(-4)
                .reverse()
                .map((action, index) => (
                  <div className="tool-batch-item" key={`${action.kind}-${actions.length - index}`}>
                    <div className="tool-batch-item-top">
                      <strong>{index === 0 ? 'Latest action' : `Earlier action ${index}`}</strong>
                    </div>
                    <div className="tool-batch-item-meta">
                      <span>{formatActionLabel(action)}</span>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="tool-live-panel-note">
              <strong>Clean workflow</strong>
              <span>
                Draw a selection, apply the edit, undo if needed, and export only when the result
                looks right.
              </span>
            </div>
          )}
        </aside>
      </div>

      {loadedImage && comparePreviewSize ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Before and after</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame tool-image-compare-frame">
              <canvas
                ref={beforeCanvasRef}
                aria-label="Original preview"
                className="tool-image-preview-canvas"
              />
            </div>
            <div className="tool-live-preview-frame tool-image-compare-frame">
              <canvas
                ref={afterCanvasRef}
                aria-label="Edited preview"
                className="tool-image-preview-canvas"
              />
            </div>
          </div>
          <p>
            {actions.length
              ? `${actions.length} edit${actions.length === 1 ? '' : 's'} applied to the current image.`
              : 'The edited preview updates as soon as you apply each selection.'}
          </p>
        </div>
      ) : null}
    </article>
  );
}
